import React from "react";
import styled, { css, keyframes } from "styled-components";
import { defaultTheme } from "../../shared/constants";

interface SpinnerProps {
  size?: number | string;
  color?: "primary" | "secondary" | "inherit";
  thickness?: number;
  variant?: "determinate" | "indeterminate";
  value?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const Spinner = ({
  size = 40,
  color = "primary",
  thickness = 3.6,
  variant = "indeterminate",
  value,
  className,
  style,
  ...props
}: SpinnerProps): JSX.Element => {
  return (
    <SpinnerContainer className={className} style={style}>
      <CircularSpinner
        $size={size}
        $color={color}
        $thickness={thickness}
        $variant={variant}
        $value={value}
        {...props}
      >
        <svg viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" />
        </svg>
      </CircularSpinner>
    </SpinnerContainer>
  );
};

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const getSpinnerColor = (color: "primary" | "secondary" | "inherit") => {
  switch (color) {
    case "primary":
      return defaultTheme.COLORS.BLACK_GREY;
    case "secondary":
      return defaultTheme.COLORS.SECONDARY || "#f50057";
    case "inherit":
      return "currentColor";
    default:
      return defaultTheme.COLORS.BLACK_GREY;
  }
};

const CircularSpinner = styled.div<{
  $size: number | string;
  $color: "primary" | "secondary" | "inherit";
  $thickness: number;
  $variant: "determinate" | "indeterminate";
  $value?: number;
}>`
  width: ${({ $size }) => (typeof $size === "number" ? `${$size}px` : $size)};
  height: ${({ $size }) => (typeof $size === "number" ? `${$size}px` : $size)};
  display: inline-block;

  ${({ $variant, $thickness, $color, $value }) => {
    const radius = 20;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray =
      $variant === "determinate" && $value !== undefined
        ? `${(circumference * $value) / 100} ${circumference}`
        : "";

    return css`
      svg {
        width: 100%;
        height: 100%;
        animation: ${$variant === "indeterminate"
          ? css`
              ${spin} 1.4s linear infinite
            `
          : "none"};
      }

      circle {
        stroke: ${getSpinnerColor($color)};
        stroke-width: ${$thickness};
        stroke-linecap: round;
        fill: none;
        r: ${radius};
        cx: 50%;
        cy: 50%;

        ${$variant === "indeterminate"
          ? css`
              stroke-dasharray: 80, 200;
              stroke-dashoffset: 0;
              animation: ${dash} 1.4s ease-in-out infinite;
            `
          : css`
              stroke-dasharray: ${strokeDasharray || `0 ${circumference}`};
              stroke-dashoffset: ${circumference / 4};
              transition: stroke-dasharray 0.3s ease;
            `}
      }
    `;
  }}
`;
